import React, { useState, useMemo } from "react"
import HeaderSection from "../../components/header-section"
import Sidebar from "../../features/sidebar/components/sidebar"
import { useGetAdminSimulationReportsWithMeasuresQuery, MeasureScore } from "../../app/services/meseeknaApiV2"
import { FiActivity, FiUsers, FiCheckCircle, FiSearch, FiClock, FiChevronDown, FiChevronUp } from "react-icons/fi"
import { MdOutlineAssessment, MdOutlineInsights } from "react-icons/md"

// Icon wrapper components for TypeScript compatibility
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const ActivityIcon = (props: { size?: number; className?: string }): JSX.Element => <FiActivity {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const UsersIcon = (props: { size?: number; className?: string }): JSX.Element => <FiUsers {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const CheckIcon = (props: { size?: number; className?: string }): JSX.Element => <FiCheckCircle {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const SearchIcon = (props: { size?: number; className?: string }): JSX.Element => <FiSearch {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const ClockIcon = (props: { size?: number; className?: string }): JSX.Element => <FiClock {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const AssessmentIcon = (props: { size?: number; className?: string }): JSX.Element => <MdOutlineAssessment {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const InsightsIcon = (props: { size?: number; className?: string }): JSX.Element => <MdOutlineInsights {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const ChevronDownIcon = (props: { size?: number; className?: string }): JSX.Element => <FiChevronDown {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const ChevronUpIcon = (props: { size?: number; className?: string }): JSX.Element => <FiChevronUp {...props} />

const getScoreColor = (score: number | null): string => {
  if (score === null) return "bg-gray-100 text-gray-600"
  if (score >= 80) return "bg-green-100 text-green-800"
  if (score >= 60) return "bg-blue-100 text-blue-800"
  if (score >= 40) return "bg-yellow-100 text-yellow-800"
  return "bg-red-100 text-red-800"
}

const MeasureScoreCard: React.FC<{ measure: MeasureScore }> = ({ measure }) => {
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-3 hover:shadow-sm transition-shadow">
      <div className="flex items-start justify-between mb-2">
        <h5 className="font-medium text-gray-900 text-sm line-clamp-1">{measure.measureName}</h5>
        {measure.measureCode && (
          <span className="text-xs text-gray-400 font-mono ml-2">{measure.measureCode}</span>
        )}
      </div>
      <div className="flex items-center gap-3">
        <div className={`px-2 py-1 rounded-lg ${getScoreColor(measure.averageScore)}`}>
          <span className="text-lg font-bold">
            {measure.averageScore !== null ? measure.averageScore.toFixed(1) : "N/A"}
          </span>
          <span className="text-xs ml-1">avg</span>
        </div>
        <div className="flex-1 text-xs text-gray-500">
          <div className="flex justify-between">
            <span>Min: {measure.minScore ?? "N/A"}</span>
            <span>Max: {measure.maxScore ?? "N/A"}</span>
          </div>
          <div className="text-gray-400">
            {measure.scoreCount} score{measure.scoreCount !== 1 ? "s" : ""}
          </div>
        </div>
      </div>
    </div>
  )
}

const SimReportsPage: React.FC = () => {
  const { data, isLoading } = useGetAdminSimulationReportsWithMeasuresQuery()
  const [searchQuery, setSearchQuery] = useState("")
  const [expandedId, setExpandedId] = useState<number | null>(null)
  const [showMeasuresId, setShowMeasuresId] = useState<number | null>(null)

  const reports = data?.data || []

  const filteredReports = useMemo(() => {
    return reports.filter((sim) =>
      sim.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (sim.shortName && sim.shortName.toLowerCase().includes(searchQuery.toLowerCase()))
    )
  }, [reports, searchQuery])

  const totalStats = useMemo(() => {
    return {
      totalSimulations: reports.length,
      totalAssignments: reports.reduce((sum, sim) => sum + sim.totalAssignments, 0),
      totalCompleted: reports.reduce((sum, sim) => sum + sim.completedAssignments, 0),
      totalActive: reports.reduce((sum, sim) => sum + sim.activeAssignments, 0),
      totalMeasuresTracked: new Set(reports.flatMap(sim => sim.measureScores.map(m => m.measureId))).size,
    }
  }, [reports])

  const getCompletionRate = (completed: number, total: number) => {
    if (total === 0) return 0
    return Math.round((completed / total) * 100)
  }

  return (
    <div className="flex bg-[#F9FAFB] min-h-screen w-full overflow-hidden">
      <Sidebar />
      <div className="flex flex-col flex-1 overflow-hidden">
        <HeaderSection />
        <main className="flex-1 overflow-y-auto p-6">
          <div className="max-w-7xl mx-auto">
            {/* Page Header */}
            <div className="mb-8">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-indigo-100 rounded-lg">
                  <AssessmentIcon size={24} className="text-indigo-600" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">
                    Simulation Reports with Measures
                  </h1>
                  <p className="text-gray-500">
                    View simulation usage, performance metrics, and measure scores
                  </p>
                </div>
              </div>
            </div>

            {/* Summary Stats */}
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-indigo-100 rounded-lg">
                    <AssessmentIcon size={20} className="text-indigo-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">{totalStats.totalSimulations}</p>
                    <p className="text-sm text-gray-500">Simulations</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <UsersIcon size={20} className="text-purple-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">{totalStats.totalAssignments}</p>
                    <p className="text-sm text-gray-500">Total Assignments</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-cyan-100 rounded-lg">
                    <ActivityIcon size={20} className="text-cyan-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">{totalStats.totalActive}</p>
                    <p className="text-sm text-gray-500">In Progress</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <CheckIcon size={20} className="text-green-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">{totalStats.totalCompleted}</p>
                    <p className="text-sm text-gray-500">Completed</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-teal-100 rounded-lg">
                    <InsightsIcon size={20} className="text-teal-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">{totalStats.totalMeasuresTracked}</p>
                    <p className="text-sm text-gray-500">Measures Tracked</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Search */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
              <div className="relative">
                <SearchIcon size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search simulations..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
            </div>

            {/* Simulation Cards */}
            {isLoading ? (
              <div className="space-y-4">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 animate-pulse">
                    <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
                    <div className="grid grid-cols-4 gap-4">
                      <div className="h-12 bg-gray-200 rounded"></div>
                      <div className="h-12 bg-gray-200 rounded"></div>
                      <div className="h-12 bg-gray-200 rounded"></div>
                      <div className="h-12 bg-gray-200 rounded"></div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="space-y-4">
                {filteredReports.map((sim) => (
                  <div
                    key={sim.id}
                    className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
                  >
                    <div
                      className="p-6 cursor-pointer hover:bg-gray-50 transition-colors"
                      onClick={() => setExpandedId(expandedId === sim.id ? null : sim.id)}
                    >
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900">{sim.title}</h3>
                          {sim.shortName && (
                            <p className="text-sm text-gray-500">{sim.shortName}</p>
                          )}
                        </div>
                        <div className="flex items-center gap-3">
                          {sim.measureScores.length > 0 && (
                            <span className="flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-teal-100 text-teal-800">
                              <InsightsIcon size={12} />
                              {sim.measureScores.length} measures
                            </span>
                          )}
                          <span
                            className={`px-3 py-1 rounded-full text-xs font-medium ${
                              sim.active === "Y" ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-800"
                            }`}
                          >
                            {sim.active === "Y" ? "Active" : "Inactive"}
                          </span>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="bg-gray-50 rounded-lg p-3">
                          <div className="flex items-center gap-2 text-gray-600 mb-1">
                            <UsersIcon size={14} />
                            <span className="text-xs">Total Assignments</span>
                          </div>
                          <p className="text-xl font-bold text-gray-900">{sim.totalAssignments}</p>
                        </div>
                        <div className="bg-cyan-50 rounded-lg p-3">
                          <div className="flex items-center gap-2 text-cyan-600 mb-1">
                            <ActivityIcon size={14} />
                            <span className="text-xs">In Progress</span>
                          </div>
                          <p className="text-xl font-bold text-cyan-700">{sim.activeAssignments}</p>
                        </div>
                        <div className="bg-green-50 rounded-lg p-3">
                          <div className="flex items-center gap-2 text-green-600 mb-1">
                            <CheckIcon size={14} />
                            <span className="text-xs">Completed</span>
                          </div>
                          <p className="text-xl font-bold text-green-700">{sim.completedAssignments}</p>
                        </div>
                        <div className="bg-purple-50 rounded-lg p-3">
                          <div className="flex items-center gap-2 text-purple-600 mb-1">
                            <ClockIcon size={14} />
                            <span className="text-xs">Avg. Duration</span>
                          </div>
                          <p className="text-xl font-bold text-purple-700">
                            {sim.averageCompletionMinutes ? `${sim.averageCompletionMinutes}m` : "N/A"}
                          </p>
                        </div>
                      </div>

                      {/* Completion Rate Bar */}
                      <div className="mt-4">
                        <div className="flex justify-between text-sm text-gray-600 mb-1">
                          <span>Completion Rate</span>
                          <span>{getCompletionRate(sim.completedAssignments, sim.totalAssignments)}%</span>
                        </div>
                        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-green-500 rounded-full transition-all duration-500"
                            style={{
                              width: `${getCompletionRate(sim.completedAssignments, sim.totalAssignments)}%`,
                            }}
                          ></div>
                        </div>
                      </div>
                    </div>

                    {/* Expanded Section */}
                    {expandedId === sim.id && (
                      <div className="border-t border-gray-200">
                        {/* Organization Usage */}
                        {sim.organizationUsage.length > 0 && (
                          <div className="p-4 bg-gray-50 border-b border-gray-200">
                            <h4 className="font-medium text-gray-700 mb-3">Usage by Organization</h4>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                              {sim.organizationUsage.map((org) => (
                                <div
                                  key={org.orgId}
                                  className="bg-white rounded-lg border border-gray-200 p-3"
                                >
                                  <p className="font-medium text-gray-900 text-sm truncate">
                                    {org.orgName}
                                  </p>
                                  <p className="text-lg font-bold text-indigo-600">{org.count}</p>
                                  <p className="text-xs text-gray-500">assignments</p>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Measure Scores Section */}
                        <div className="p-4 bg-white">
                          <div
                            className="flex items-center justify-between cursor-pointer"
                            onClick={(e) => {
                              e.stopPropagation()
                              setShowMeasuresId(showMeasuresId === sim.id ? null : sim.id)
                            }}
                          >
                            <div className="flex items-center gap-2">
                              <InsightsIcon size={18} className="text-teal-600" />
                              <h4 className="font-medium text-gray-700">
                                Measure Scores ({sim.measureScores.length} measures)
                              </h4>
                            </div>
                            <button className="text-gray-500 hover:text-gray-700">
                              {showMeasuresId === sim.id ? (
                                <ChevronUpIcon size={20} />
                              ) : (
                                <ChevronDownIcon size={20} />
                              )}
                            </button>
                          </div>

                          {showMeasuresId === sim.id && (
                            <div className="mt-4">
                              {sim.measureScores.length > 0 ? (
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                                  {sim.measureScores.map((measure) => (
                                    <MeasureScoreCard key={measure.measureId} measure={measure} />
                                  ))}
                                </div>
                              ) : (
                                <div className="text-center py-6 text-gray-500">
                                  <InsightsIcon size={32} className="mx-auto text-gray-300 mb-2" />
                                  <p>No measure scores recorded for this simulation yet.</p>
                                  <p className="text-sm text-gray-400">
                                    Scores will appear when users complete the simulation.
                                  </p>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
                {filteredReports.length === 0 && (
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
                    <AssessmentIcon size={48} className="mx-auto text-gray-300 mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                      No Simulations Found
                    </h3>
                    <p className="text-gray-500">
                      No simulations match your search criteria.
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  )
}

export default SimReportsPage
